/* eslint-disable max-lines */

import React, { type FC, useState, useEffect, useRef } from "react";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";
import { DateInput } from "./date-input";
import { Label } from "./label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Switch } from "./switch";
import { ChevronUpIcon, ChevronDownIcon, CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DateRangePickerProps {
  /** Click handler for applying the updates from DateRangePicker. */
  onUpdate?: (values: {
    range: DateRangeTimestamp;
    rangeCompare?: DateRangeTimestamp;
  }) => void;
  /** Initial value for start date */
  initialDateFrom?: Date | string | number;
  /** Initial value for end date */
  initialDateTo?: Date | string | number;
  /** Initial value for start date for compare */
  initialCompareFrom?: Date | string | number;
  /** Initial value for end date for compare */
  initialCompareTo?: Date | string | number;
  /** Alignment of popover */
  align?: "start" | "center" | "end";
  /** Option for locale */
  locale?: string;
  /** Option for showing compare feature */
  showCompare?: boolean;
  /** Trigger display variant: 'icon' shows only icon on small screens (default), 'full' always shows date text */
  triggerVariant?: "icon" | "full";
  /** Custom trigger element to replace the default button */
  trigger?: React.ReactNode;
}

const formatDate = (date: Date, locale: string = "es-ES"): string => {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const getDateAdjustedForTimezone = (
  dateInput: Date | string | number,
): Date => {
  if (typeof dateInput === "number") {
    // If it's a timestamp, convert to Date
    return new Date(dateInput);
  } else if (typeof dateInput === "string") {
    // Split the date string to get year, month, and day parts
    const parts = dateInput.split("-").map((part) => parseInt(part, 10));
    // Create a new Date object using the local timezone
    // Note: Month is 0-indexed, so subtract 1 from the month part
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date;
  } else {
    // If dateInput is already a Date object, return it directly
    return dateInput;
  }
};

interface DateRange {
  from: Date;
  to: Date | undefined;
}

export interface DateRangeTimestamp {
  from: number;
  to: number | undefined;
}

interface Preset {
  name: string;
  label: string;
}

// Define presets en español
const PRESETS: Preset[] = [
  { name: "today", label: "Hoy" },
  { name: "yesterday", label: "Ayer" },
  { name: "last7", label: "Últimos 7 días" },
  { name: "last14", label: "Últimos 14 días" },
  { name: "last30", label: "Últimos 30 días" },
  { name: "thisWeek", label: "Esta semana" },
  { name: "lastWeek", label: "Semana pasada" },
  { name: "thisMonth", label: "Este mes" },
  { name: "lastMonth", label: "Mes pasado" },
];

/** The DateRangePicker component allows a user to select a range of dates */
export const DateRangePicker: FC<DateRangePickerProps> & {
  filePath: string;
} = ({
  initialDateFrom = new Date(0), // January 1, 1970 (epoch)
  initialDateTo = new Date(), // La fecha actual
  initialCompareFrom,
  initialCompareTo,
  onUpdate,
  align = "end",
  locale = "es-ES",
  showCompare = true,
  triggerVariant = "icon",
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [range, setRange] = useState<DateRange>({
    from: getDateAdjustedForTimezone(initialDateFrom),
    to: initialDateTo
      ? getDateAdjustedForTimezone(initialDateTo)
      : getDateAdjustedForTimezone(initialDateFrom),
  });
  const [rangeCompare, setRangeCompare] = useState<DateRange | undefined>(
    initialCompareFrom
      ? {
          from: getDateAdjustedForTimezone(initialCompareFrom),
          to: initialCompareTo
            ? getDateAdjustedForTimezone(initialCompareTo)
            : getDateAdjustedForTimezone(initialCompareFrom),
        }
      : undefined,
  );

  // Refs to store the values of range and rangeCompare when the date picker is opened
  const openedRangeRef = useRef<DateRange | undefined>(undefined);
  const openedRangeCompareRef = useRef<DateRange | undefined>(undefined);

  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(
    undefined,
  );

  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getPresetRange = (presetName: string): DateRange => {
    const preset = PRESETS.find(({ name }) => name === presetName);
    if (!preset) throw new Error(`Unknown date range preset: ${presetName}`);
    const from = new Date();
    const to = new Date();
    const first = from.getDate() - from.getDay();

    switch (preset.name) {
      case "today":
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case "yesterday":
        from.setDate(from.getDate() - 1);
        from.setHours(0, 0, 0, 0);
        to.setDate(to.getDate() - 1);
        to.setHours(23, 59, 59, 999);
        break;
      case "last7":
        from.setDate(from.getDate() - 6);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case "last14":
        from.setDate(from.getDate() - 13);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case "last30":
        from.setDate(from.getDate() - 29);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case "thisWeek":
        from.setDate(first);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case "lastWeek":
        from.setDate(from.getDate() - 7 - from.getDay());
        to.setDate(to.getDate() - to.getDay() - 1);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case "thisMonth":
        from.setDate(1);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      case "lastMonth":
        from.setMonth(from.getMonth() - 1);
        from.setDate(1);
        from.setHours(0, 0, 0, 0);
        to.setDate(0);
        to.setHours(23, 59, 59, 999);
        break;
    }

    return { from, to };
  };

  const setPreset = (preset: string): void => {
    const range = getPresetRange(preset);
    setRange(range);
    if (rangeCompare) {
      const rangeCompare = {
        from: new Date(
          range.from.getFullYear() - 1,
          range.from.getMonth(),
          range.from.getDate(),
        ),
        to: range.to
          ? new Date(
              range.to.getFullYear() - 1,
              range.to.getMonth(),
              range.to.getDate(),
            )
          : undefined,
      };
      setRangeCompare(rangeCompare);
    }
  };

  const checkPreset = (): void => {
    for (const preset of PRESETS) {
      const presetRange = getPresetRange(preset.name);

      const normalizedRangeFrom = new Date(range.from);
      normalizedRangeFrom.setHours(0, 0, 0, 0);
      const normalizedPresetFrom = new Date(
        presetRange.from.setHours(0, 0, 0, 0),
      );

      const normalizedRangeTo = new Date(range.to ?? 0);
      normalizedRangeTo.setHours(0, 0, 0, 0);
      const normalizedPresetTo = new Date(
        presetRange.to?.setHours(0, 0, 0, 0) ?? 0,
      );

      if (
        normalizedRangeFrom.getTime() === normalizedPresetFrom.getTime() &&
        normalizedRangeTo.getTime() === normalizedPresetTo.getTime()
      ) {
        setSelectedPreset(preset.name);
        return;
      }
    }

    setSelectedPreset(undefined);
  };

  const resetValues = (): void => {
    setRange({
      from: getDateAdjustedForTimezone(
        typeof initialDateFrom === "string" ||
          typeof initialDateFrom === "number"
          ? initialDateFrom
          : initialDateFrom,
      ),
      to: initialDateTo
        ? getDateAdjustedForTimezone(
            typeof initialDateTo === "string" ||
              typeof initialDateTo === "number"
              ? initialDateTo
              : initialDateTo,
          )
        : getDateAdjustedForTimezone(
            typeof initialDateFrom === "string" ||
              typeof initialDateFrom === "number"
              ? initialDateFrom
              : initialDateFrom,
          ),
    });
    setRangeCompare(
      initialCompareFrom
        ? {
            from: getDateAdjustedForTimezone(
              typeof initialCompareFrom === "string"
                ? initialCompareFrom
                : initialCompareFrom instanceof Date
                  ? initialCompareFrom
                  : new Date(initialCompareFrom),
            ),
            to: initialCompareTo
              ? getDateAdjustedForTimezone(
                  typeof initialCompareTo === "string"
                    ? initialCompareTo
                    : initialCompareTo instanceof Date
                      ? initialCompareTo
                      : new Date(initialCompareTo),
                )
              : getDateAdjustedForTimezone(
                  typeof initialCompareFrom === "string"
                    ? initialCompareFrom
                    : initialCompareFrom instanceof Date
                      ? initialCompareFrom
                      : new Date(initialCompareFrom),
                ),
          }
        : undefined,
    );
  };

  useEffect(() => {
    checkPreset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  // Helper function to check if two date ranges are equal
  const areRangesEqual = (a?: DateRange, b?: DateRange): boolean => {
    if (!a || !b) return a === b;
    return (
      a.from.getTime() === b.from.getTime() &&
      (!a.to || !b.to || a.to.getTime() === b.to.getTime())
    );
  };

  useEffect(() => {
    if (isOpen) {
      openedRangeRef.current = range;
      openedRangeCompareRef.current = rangeCompare;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function toInputDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  function fromInputDate(value: string): Date {
    const [y, mo, d] = value.split("-").map(Number);
    return new Date(y, mo - 1, d);
  }

  function emitUpdate(from: Date, to: Date | undefined) {
    onUpdate?.({
      range: { from: from.getTime(), to: to?.getTime() },
      rangeCompare: undefined,
    });
  }

  // Mobile: two visible styled native date inputs, side by side
  if (isSmallScreen) {
    return (
      <div className="flex gap-2 w-full">
        <div className="flex-1 flex flex-col gap-1">
          <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
            Desde
          </span>
          <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground/60 flex-shrink-0" />
            <input
              type="date"
              value={toInputDate(range.from)}
              onChange={(e) => {
                if (!e.target.value) return;
                const from = fromInputDate(e.target.value);
                const to = range.to && from > range.to ? from : range.to;
                setRange({ from, to });
                emitUpdate(from, to);
              }}
              className="flex-1 bg-transparent text-xs font-medium border-none outline-none min-w-0 text-foreground"
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-1">
          <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
            Hasta
          </span>
          <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground/60 flex-shrink-0" />
            <input
              type="date"
              value={range.to ? toInputDate(range.to) : ""}
              min={toInputDate(range.from)}
              onChange={(e) => {
                if (!e.target.value) return;
                const to = fromInputDate(e.target.value);
                const from = to < range.from ? to : range.from;
                setRange({ from, to });
                emitUpdate(from, to);
              }}
              className="flex-1 bg-transparent text-xs font-medium border-none outline-none min-w-0 text-foreground"
            />
          </div>
        </div>
      </div>
    );
  }

  const defaultTriggerButton = (
    <Button
      variant="outline"
      className={cn(
        "justify-start text-left font-normal hover:bg-none",
        triggerVariant === "icon" && "2xl:min-w-[200px]",
        triggerVariant === "full" && "w-full min-w-[200px]",
        !range && "text-muted-foreground",
      )}
    >
      <CalendarIcon
        className={cn(
          "h-4 w-4 opacity-50",
          triggerVariant === "icon" && "2xl:mr-3",
          triggerVariant === "full" && "mr-3",
        )}
      />
      <div
        className={cn(
          "flex flex-col gap-1 flex-1",
          triggerVariant === "icon" && "hidden 2xl:flex",
        )}
      >
        <div className="text-sm font-medium">
          {`${formatDate(range.from, locale)}${
            range.to != null ? " - " + formatDate(range.to, locale) : ""
          }`}
        </div>
        {rangeCompare != null && (
          <div className="text-xs text-muted-foreground">
            vs. {formatDate(rangeCompare.from, locale)}
            {rangeCompare.to != null
              ? ` - ${formatDate(rangeCompare.to, locale)}`
              : ""}
          </div>
        )}
      </div>
      <div
        className={cn(
          "ml-2 opacity-50",
          triggerVariant === "icon" && "hidden 2xl:block",
        )}
      >
        {isOpen ? (
          <ChevronUpIcon className="h-4 w-4" />
        ) : (
          <ChevronDownIcon className="h-4 w-4" />
        )}
      </div>
    </Button>
  );

  const handleApply = () => {
    setIsOpen(false);
    if (
      !areRangesEqual(range, openedRangeRef.current) ||
      !areRangesEqual(rangeCompare, openedRangeCompareRef.current)
    ) {
      const rangeTimestamp: DateRangeTimestamp = {
        from: range.from.getTime(),
        to: range.to?.getTime(),
      };
      const rangeCompareTimestamp: DateRangeTimestamp | undefined = rangeCompare
        ? {
            from: rangeCompare.from.getTime(),
            to: rangeCompare.to?.getTime(),
          }
        : undefined;
      onUpdate?.({
        range: rangeTimestamp,
        rangeCompare: rangeCompareTimestamp,
      });
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    resetValues();
  };

  const pickerControls = (
    <div className="flex flex-col p-3 space-y-3">
      {/* Header con toggle de comparación */}
      <div className="flex flex-col gap-2 pb-2 border-b">
        <div>
          <h4 className="font-semibold text-sm">Seleccionar período</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Elige un rango de fechas
          </p>
        </div>
        {showCompare && (
          <div className="flex items-center space-x-2 pt-1">
            <Switch
              checked={Boolean(rangeCompare)}
              onCheckedChange={(checked: boolean) => {
                if (checked) {
                  if (!range.to) {
                    setRange({ from: range.from, to: range.from });
                  }
                  setRangeCompare({
                    from: new Date(
                      range.from.getFullYear(),
                      range.from.getMonth(),
                      range.from.getDate() - 365,
                    ),
                    to: range.to
                      ? new Date(
                          range.to.getFullYear() - 1,
                          range.to.getMonth(),
                          range.to.getDate(),
                        )
                      : new Date(
                          range.from.getFullYear() - 1,
                          range.from.getMonth(),
                          range.from.getDate(),
                        ),
                  });
                } else {
                  setRangeCompare(undefined);
                }
              }}
              id="compare-mode"
            />
            <Label
              htmlFor="compare-mode"
              className="text-sm font-medium cursor-pointer"
            >
              Comparar
            </Label>
          </div>
        )}
      </div>

      {/* Inputs de fecha */}
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-muted-foreground mb-2 block">
            Período principal
          </Label>
          <div className="flex flex-col gap-2">
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Fecha desde
              </Label>
              <DateInput
                value={range.from}
                onChange={(date) => {
                  const toDate =
                    range.to == null || date > range.to ? date : range.to;
                  setRange((prevRange) => ({
                    ...prevRange,
                    from: date,
                    to: toDate,
                  }));
                }}
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Fecha hasta
              </Label>
              <DateInput
                value={range.to}
                onChange={(date) => {
                  const fromDate = date < range.from ? date : range.from;
                  setRange((prevRange) => ({
                    ...prevRange,
                    from: fromDate,
                    to: date,
                  }));
                }}
              />
            </div>
          </div>
        </div>

        {rangeCompare != null && (
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">
              Período de comparación
            </Label>
            <div className="flex flex-col gap-2">
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  Fecha desde
                </Label>
                <DateInput
                  value={rangeCompare?.from}
                  onChange={(date) => {
                    if (rangeCompare) {
                      const compareToDate =
                        rangeCompare.to == null || date > rangeCompare.to
                          ? date
                          : rangeCompare.to;
                      setRangeCompare((prevRangeCompare) => ({
                        ...prevRangeCompare,
                        from: date,
                        to: compareToDate,
                      }));
                    } else {
                      setRangeCompare({ from: date, to: new Date() });
                    }
                  }}
                />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  Fecha hasta
                </Label>
                <DateInput
                  value={rangeCompare?.to}
                  onChange={(date) => {
                    if (rangeCompare && rangeCompare.from) {
                      const compareFromDate =
                        date < rangeCompare.from ? date : rangeCompare.from;
                      setRangeCompare({
                        ...rangeCompare,
                        from: compareFromDate,
                        to: date,
                      });
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Select de accesos rápidos */}
      <div>
        <Label className="text-xs text-muted-foreground mb-2 block">
          Accesos rápidos
        </Label>
        <Select
          value={selectedPreset}
          onValueChange={(value) => {
            setPreset(value);
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="A partir de..." />
          </SelectTrigger>
          <SelectContent>
            {PRESETS.map((preset) => (
              <SelectItem key={preset.name} value={preset.name}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const pickerCalendar = (
    <Calendar
      mode="range"
      onSelect={(value: { from?: Date; to?: Date } | undefined) => {
        if (value?.from != null) {
          setRange({ from: value.from, to: value?.to });
        }
      }}
      selected={range}
      numberOfMonths={1}
      defaultMonth={new Date()}
    />
  );

  const actionButtons = (
    <>
      <Button onClick={handleCancel} variant="outline" size="sm">
        Cancelar
      </Button>
      <Button onClick={handleApply} size="sm">
        Aplicar
      </Button>
    </>
  );

  // Desktop: Popover
  return (
    <Popover
      modal={true}
      open={isOpen}
      onOpenChange={(open: boolean) => {
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>{trigger || defaultTriggerButton}</PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0 z-[9999]">
        <div className="flex flex-row">
          <div className="min-w-[180px]">{pickerControls}</div>
          <div className="border-l">
            <div className="p-2">{pickerCalendar}</div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 p-4 border-t bg-muted/30">
          <div className="text-xs text-muted-foreground flex-1">
            {selectedPreset && (
              <span className="font-medium">
                {PRESETS.find((p) => p.name === selectedPreset)?.label}
              </span>
            )}
          </div>
          <div className="flex gap-2">{actionButtons}</div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

DateRangePicker.filePath = "components/ui/date-range-picker.tsx";
